"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ok = ok;
exports.err = err;
exports.handleUnknownCaughtError = handleUnknownCaughtError;
exports.catchError = catchError;
exports.tryAsync = tryAsync;
exports.resultifyAsync = resultifyAsync;
exports.resultify = resultify;
exports.extendPromiseWithResultMethods = extendPromiseWithResultMethods;
const CodedError_1 = require("./CodedError");
const Promise_1 = require("./Promise");
const Result_1 = require("./Result");
/**
 * Helper to construct an Ok result
 *
 * @template ValueType - The type of the value in the result
 * @template ErrorType - The type of the error in the result
 * @param value - The success value of the result
 * @returns A new Ok result with the value
 */
function ok(value) {
    return new Result_1.Ok(value);
}
/**
 * Helper to construct an Err result
 *
 * @template ValueType - The type of the value in the result
 * @template ErrorType extends Error - The type of the error in the result
 * @param error - The error value of the result
 * @returns A new Err result with the error
 */
function err(error) {
    return new Result_1.Err(error);
}
/**
 * Handle errors Caught using try catch block
 *
 * @param error - Error caught in try catch block
 * @returns An instance of {@link UnknownError} from the error,
 * with the originally caught error available in error.details
 */
function handleUnknownCaughtError(error) {
    if (error instanceof CodedError_1.UnknownError) {
        return error;
    }
    else if (error instanceof Error) {
        return new CodedError_1.UnknownError(error.message, { error });
    }
    else if (typeof error === 'string') {
        return new CodedError_1.UnknownError(error, { error });
    }
    return new CodedError_1.UnknownError('Unknown error', { error });
}
// Implementation of the catchError function
// Documentation is provided with the function signatures
function catchError(fn, errorHandler) {
    try {
        const result = fn();
        return ok(result);
    }
    catch (error) {
        if (errorHandler) {
            return err(errorHandler(error));
        }
        const unknownError = handleUnknownCaughtError(error);
        return err(unknownError);
    }
}
// Implementation of tryAsync
// Documentation is provided with the function signatures
function tryAsync(promise, errorHandler) {
    const processError = (error) => {
        if (errorHandler) {
            try {
                return errorHandler(error);
            }
            catch (errorHandlerError) {
                const unknownError = handleUnknownCaughtError(errorHandlerError);
                return unknownError;
            }
        }
        const unknownError = handleUnknownCaughtError(error);
        return unknownError;
    };
    return new Promise_1.ResultifiedPromise((resolve) => {
        try {
            Promise.resolve(promise).then((value) => resolve(ok(value)), (error) => resolve(err(processError(error))));
        }
        catch (error) {
            resolve(err(processError(error)));
        }
    });
}
// This is just the Implementation
// Documentation is provided with the function signatures
function resultifyAsync(asyncFn, errorHandler) {
    const processError = (error, ...args) => {
        if (errorHandler) {
            try {
                return errorHandler(error, ...args);
            }
            catch (errorHandlerError) {
                const unknownError = handleUnknownCaughtError(errorHandlerError);
                return unknownError;
            }
        }
        const unknownError = handleUnknownCaughtError(error);
        return unknownError;
    };
    return (...args) => {
        return new Promise_1.ResultifiedPromise((resolve) => {
            try {
                const promise = asyncFn(...args);
                Promise.resolve(promise).then((value) => resolve(ok(value)), (error) => resolve(err(processError(error, ...args))));
            }
            catch (error) {
                resolve(err(processError(error, ...args)));
            }
        });
    };
}
/**
 * Converts a sync function to return a Result function
 *
 * @template ErrorType - The type of the error in the result
 * @param syncFn - The synchronous function to be converted to a Result function
 * @param errorHandler - The function to handle the error if the promise gets rejected
 * @returns A function that returns a Result with the same signature as the original function
 */
function resultify(syncFn, errorHandler) {
    return (...args) => {
        try {
            const result = syncFn(...args);
            return ok(result);
        }
        catch (error) {
            if (errorHandler) {
                return err(errorHandler(error, ...args));
            }
            const unknownError = handleUnknownCaughtError(error);
            return err(unknownError);
        }
    };
}
/**
 * Extends the global Promise prototype to add the `unwrapResult` and `intoResult` methods.
 *
 * - The `unwrapResult` method converts a Promise that resolves to a Result into a ResultifiedPromise,
 * allowing for easier access to the Result methods without awaiting the promise first.
 *
 * - The `intoResult` method catches any error thrown by the promise and resolves it to a `ResultifiedPromise`.
 *
 * This function should be called during application initialization to
 * ensure that the extension methods are available on all Promise instances.
 *
 * @example
 * ```ts
 * extendPromiseWithResultMethods();
 *
 * const myPromise = fetchData(); // Assume fetchData returns a Promise
 * const [value, error] = await myPromise.unwrapResult().toTuple();
 * ```
 */
function extendPromiseWithResultMethods() {
    if (!Promise.prototype.unwrapResult) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        Object.defineProperty(Promise.prototype, 'unwrapResult', {
            value: function () {
                return new Promise_1.ResultifiedPromise((resolve, reject) => {
                    this.then((value) => resolve(value)).catch((err) => reject(err));
                });
            },
            writable: false,
            configurable: false,
            enumerable: false,
        });
    }
    if (!Promise.prototype.intoResult) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        Object.defineProperty(Promise.prototype, 'intoResult', {
            value: function (errorHandler) {
                return tryAsync(this, errorHandler);
            },
            writable: false,
            configurable: false,
            enumerable: false,
        });
    }
}
//# sourceMappingURL=Helpers.js.map